/**
 * The panel frame — §14.8: a SIDEBAR replaces the horizontal nav the six §7.2
 * screens shipped with, because V2 has twelve screens and a horizontal strip
 * that wraps onto a second line is a navigation that moves under the cursor.
 *
 * NAV ENTRIES ARE GATED BY `can()`, NEVER BY ROLE ORDER (D-032 §4.0). An entry
 * a role has no capability for is **not rendered at all** — not greyed out, not
 * disabled with a tooltip. A disabled control is a statement that the feature
 * exists and is being withheld; absence is the truth for a `content_editor` who
 * simply does not do case work. The server enforces every one of these gates
 * independently; this is the courtesy, not the control.
 *
 * Only round-A and already-shipped screens are listed. The rest of §14.8's
 * inventory joins this array with its own round, so the sidebar never advertises
 * a route that answers 404.
 */
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import type { AdminCapability } from '@navoiy/domain';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { SummaryProvider } from '../lib/useSummary.jsx';
import { Button } from '../ui/index.jsx';
import { PasswordDialog } from './PasswordDialog.jsx';
import { TopBar } from './TopBar.jsx';

interface NavEntry {
  to: string;
  labelKey: string;
  capability: AdminCapability;
  /** `/` matches every route unless the match is exact. */
  end?: boolean;
}

const NAV: NavEntry[] = [
  { to: '/', labelKey: 'admin.nav.dashboard', capability: 'analytics.read', end: true },
  { to: '/murojaats', labelKey: 'admin.nav.inbox', capability: 'inbox.list' },
  // §14.8 screen 4 round C. Directly under the inbox because a walk-in ends up
  // in the same queue — filing is the front door to the list above it.
  { to: '/murojaats/new', labelKey: 'admin.nav.reception', capability: 'appeal.create' },
  // §14.8 round B. Each entry names the capability the SCREEN needs, so a
  // `viewer` sees the dashboard, the mahallas and the statistics and none of
  // the four screens below that identify a person or a colleague.
  { to: '/appellants', labelKey: 'admin.nav.appellants', capability: 'appellant.list' },
  { to: '/mahallas', labelKey: 'admin.nav.mahallas', capability: 'analytics.read' },
  { to: '/stats', labelKey: 'admin.nav.stats', capability: 'analytics.read' },
  { to: '/executors', labelKey: 'admin.nav.executors', capability: 'analytics.staff' },
  // §14.8 screen 8 round C.
  { to: '/ai', labelKey: 'admin.nav.ai', capability: 'ai.read' },
  { to: '/reports', labelKey: 'admin.nav.reports', capability: 'analytics.export' },
  { to: '/audit', labelKey: 'admin.nav.audit', capability: 'audit.read' },
  { to: '/directory', labelKey: 'admin.nav.directory', capability: 'directory.read' },
  { to: '/devices', labelKey: 'admin.nav.devices', capability: 'device.read' },
  { to: '/users', labelKey: 'admin.nav.users', capability: 'user.manage' },
];

export function AppShell(): React.JSX.Element {
  const { me, allows } = useSession();
  const { t } = useI18n();
  const [passwordOpen, setPasswordOpen] = useState(false);

  return (
    <SummaryProvider>
      <div className="flex min-h-full">
        {/*
          `print:hidden` — §14.5.4 step 5's receipt is a browser print of the
          main region, and a slip carrying the whole navigation frame is not a
          receipt. The screen that prints decides what to show; the shell only
          gets out of the way.
        */}
        <nav className="flex w-60 shrink-0 flex-col gap-1 bg-header-band px-3 py-4 text-ondark-primary print:hidden">
          <div className="px-2 pb-4">
            <p className="text-xs uppercase tracking-eyebrow text-ondark-secondary">
              {t('admin.login.subtitle')}
            </p>
          </div>

          {NAV.filter((entry) => allows(entry.capability)).map((entry) => (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={entry.end}
              className={({ isActive }) =>
                `rounded-btn px-3 py-2 text-sm font-semibold transition-colors duration-fast ${
                  isActive
                    ? 'bg-ondark-fillPress text-ondark-primary'
                    : 'text-ondark-secondary hover:bg-ondark-fill'
                }`
              }
            >
              {t(entry.labelKey)}
            </NavLink>
          ))}

          <div className="mt-auto flex flex-col gap-2 pt-6">
            <div className="border-t-1 border-ondark-rule px-2 pt-3">
              <p className="text-sm font-semibold">{me?.full_name}</p>
              <p className="text-xs text-ondark-secondary">
                {me ? t(`admin.role.${me.role}`) : null}
              </p>
            </div>
            <Button
              tone="ghost"
              className="justify-start text-ondark-secondary hover:bg-ondark-fill"
              onClick={() => setPasswordOpen(true)}
            >
              {t('admin.nav.account')}
            </Button>

            {/*
              §14.7 — the personal-data-law notice. A static string in the shell,
              in all four locales, on every screen: the panel displays citizens'
              names, addresses and phone numbers, and the staff member looking at
              them should be told so where they are working rather than in a
              policy document they signed once.
            */}
            <p className="px-2 pt-2 text-xs leading-snug text-ondark-secondary">
              {t('admin.legal.banner')}
            </p>
          </div>
        </nav>

        <div className="flex min-w-0 flex-1 flex-col bg-surface-50 print:bg-transparent">
          <div className="print:hidden">
            <TopBar />
          </div>
          <main className="w-full flex-1 px-6 py-6 print:px-0 print:py-0">
            <Outlet />
          </main>
        </div>

        {passwordOpen && me ? (
          <PasswordDialog userId={me.id} self onClose={() => setPasswordOpen(false)} />
        ) : null}
      </div>
    </SummaryProvider>
  );
}
